import { useMemo } from 'react';
import { type VehicleCycledEnergyRecord } from '../services/battery-monitoring-api/types/battery-monitoring-api';

interface UseFilteredVehiclesParameters {
  vehicles?: VehicleCycledEnergyRecord[];
  selectedCountries: string[];
  selectedCustomers: string[];
  selectedChassisFilters: string[];
  selectedBatteries: string[];
  selectedConfigurations: string[];
  selectedVehicleTypes: string[];
  selectedPropulsions: string[];
  showAlerts: boolean;
}

const useBaseFilteredVehicles = ({
  vehicles,
  selectedCountries,
  selectedCustomers,
  selectedChassisFilters,
  selectedBatteries,
  selectedConfigurations,
  selectedVehicleTypes,
  selectedPropulsions,
  showAlerts,
}: UseFilteredVehiclesParameters): VehicleCycledEnergyRecord[] | undefined => {
  return useMemo(() => {
    if (!vehicles) return;

    const countrySet = new Set(selectedCountries);
    const customerSet = new Set(selectedCustomers);
    const chassisSet = new Set(selectedChassisFilters);
    const batterySet = new Set(selectedBatteries);
    const configSet = new Set(selectedConfigurations);
    const vehicleTypeSet = new Set(selectedVehicleTypes);
    const propulsionSet = new Set(selectedPropulsions);

    const hasCountryFilter = countrySet.size > 0;
    const hasCustomerFilter = customerSet.size > 0;
    const hasChassisFilter = chassisSet.size > 0;
    const hasBatteryFilter = batterySet.size > 0;
    const hasConfigurationFilter = configSet.size > 0;
    const hasVehicleTypeFilter = vehicleTypeSet.size > 0;
    const hasPropulsionFilter = propulsionSet.size > 0;

    return vehicles.filter((vehicle) => {
      const matchesCountry = !hasCountryFilter || countrySet.has(vehicle.customerCountry);
      const matchesCustomer = !hasCustomerFilter || customerSet.has(vehicle.customer);
      const matchesChassis = !hasChassisFilter || chassisSet.has(vehicle.chassisNumber);
      const matchesBattery =
        !hasBatteryFilter || !!(vehicle.batteryCapacity && batterySet.has(vehicle.batteryCapacity.toString()));
      const matchesConfiguration = !hasConfigurationFilter || configSet.has(vehicle.configuration);
      const matchesVehicleType = !hasVehicleTypeFilter || vehicleTypeSet.has(vehicle.vehicleType);
      const matchesPropulsion = !hasPropulsionFilter || propulsionSet.has(vehicle.propulsion ?? 'BEV');
      const matchesAlert = !showAlerts || vehicle.vehicleAlert;

      return (
        matchesCountry &&
        matchesCustomer &&
        matchesChassis &&
        matchesBattery &&
        matchesConfiguration &&
        matchesVehicleType &&
        matchesPropulsion &&
        matchesAlert
      );
    });
  }, [
    vehicles,
    selectedCountries,
    selectedCustomers,
    selectedChassisFilters,
    selectedBatteries,
    selectedConfigurations,
    selectedVehicleTypes,
    selectedPropulsions,
    showAlerts,
  ]);
};

export default useBaseFilteredVehicles;
